import { useState } from "react";
import {
  Box,
  Button,
  Divider,
  MenuItem,
  MenuList,
  Popover,
  TextField,
} from "@mui/material";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";

// All ranges are expressed as Israel-local "YYYY-MM-DDTHH:MM" keys so they
// compare lexically against ilDateTimeKey(clip_start_utc_ms) in the parent —
// timezone-safe regardless of the viewer's own clock.
function ilToday() {
  return new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Jerusalem" });
}
// Day arithmetic on the civil date via a UTC anchor (no DST/local-tz drift).
function addDays(dateStr, n) {
  const d = new Date(`${dateStr}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}
const monthStart = (dateStr) => `${dateStr.slice(0, 8)}01`;
const dayStart = (dateStr) => `${dateStr}T00:00`;
const dayEnd = (dateStr) => `${dateStr}T23:59`;

// Each preset resolves (lazily, against "today") to a {from,to} pair of keys.
const PRESETS = [
  { key: "all", range: () => ({ from: "", to: "" }) },
  {
    key: "today",
    range: () => {
      const t = ilToday();
      return { from: dayStart(t), to: dayEnd(t) };
    },
  },
  {
    key: "yesterday",
    range: () => {
      const y = addDays(ilToday(), -1);
      return { from: dayStart(y), to: dayEnd(y) };
    },
  },
  {
    key: "last7",
    range: () => {
      const t = ilToday();
      return { from: dayStart(addDays(t, -6)), to: dayEnd(t) };
    },
  },
  {
    key: "thisMonth",
    range: () => {
      const t = ilToday();
      return { from: dayStart(monthStart(t)), to: dayEnd(t) };
    },
  },
];

// Compact button → popover with presets + a custom From/To range. `value` is
// { preset, from, to }; `from`/`to` are Israel-local datetime keys (or "").
export default function DateRangeFilter({ value, onChange, text }) {
  const [anchor, setAnchor] = useState(null);
  // Local draft for the custom date inputs (date-only "YYYY-MM-DD"), seeded
  // from the active range so reopening shows what's applied.
  const [draftFrom, setDraftFrom] = useState("");
  const [draftTo, setDraftTo] = useState("");

  const open = (e) => {
    setDraftFrom(value.from ? value.from.slice(0, 10) : "");
    setDraftTo(value.to ? value.to.slice(0, 10) : "");
    setAnchor(e.currentTarget);
  };
  const close = () => setAnchor(null);

  const pickPreset = (p) => {
    onChange({ preset: p.key, ...p.range() });
    close();
  };
  const applyCustom = () => {
    onChange({
      preset: "custom",
      from: draftFrom ? dayStart(draftFrom) : "",
      to: draftTo ? dayEnd(draftTo) : "",
    });
    close();
  };

  // Button label: the active preset's name, or the custom range itself.
  const label =
    value.preset === "custom"
      ? `${value.from ? value.from.slice(0, 10) : "…"} – ${
          value.to ? value.to.slice(0, 10) : "…"
        }`
      : text[value.preset] || text.allTime;
  const active = value.preset && value.preset !== "all";

  return (
    <>
      <Button
        size="small"
        variant={active ? "contained" : "outlined"}
        onClick={open}
        startIcon={<CalendarMonthRoundedIcon fontSize="small" />}
        endIcon={<ExpandMoreRoundedIcon fontSize="small" />}
        sx={{ textTransform: "none", minWidth: 132, whiteSpace: "nowrap" }}
      >
        {label}
      </Button>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{ paper: { sx: { display: "flex", mt: 0.5, borderRadius: 2 } } }}
      >
        <MenuList sx={{ minWidth: 150, py: 0.5 }}>
          {PRESETS.map((p) => (
            <MenuItem
              key={p.key}
              selected={value.preset === p.key}
              onClick={() => pickPreset(p)}
              sx={{ fontSize: 13.5, fontWeight: 600 }}
            >
              {text[p.key] || text.allTime}
            </MenuItem>
          ))}
        </MenuList>
        <Divider orientation="vertical" flexItem />
        <Box
          sx={{
            p: 2,
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            minWidth: 210,
          }}
        >
          <Box sx={{ fontSize: 12, fontWeight: 700, opacity: 0.7 }}>
            {text.custom}
          </Box>
          <TextField
            size="small"
            type="date"
            label={text.from || "From"}
            value={draftFrom}
            onChange={(e) => setDraftFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            size="small"
            type="date"
            label={text.to || "To"}
            value={draftTo}
            onChange={(e) => setDraftTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Button
            size="small"
            variant="contained"
            onClick={applyCustom}
            disabled={!draftFrom && !draftTo}
          >
            {text.apply}
          </Button>
        </Box>
      </Popover>
    </>
  );
}
